import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, User, MessageSquare, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", content: "", website: "" });
  const [sending, setSending] = useState(false);
  const mountedAt = useRef(Date.now());

  const loadComments = () => api.get(`/posts/${slug}/comments`).then((r) => setComments(r.data)).catch(() => {});

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/posts/${slug}/comments`, { ...form, elapsed_ms: Date.now() - mountedAt.current });
      if (data.status === "approved") {
        toast.success("Comment posted!");
        loadComments();
      } else if (data.status === "pending") {
        toast.success("Comment submitted — it will appear after moderation.");
      } else {
        toast.success("Comment submitted.");
      }
      setForm({ name: "", email: "", content: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="blog-not-found">
        <h1 className="font-display text-3xl font-bold text-brand-emerald">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-brand-lime font-bold">← Back to blog</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="pt-40 pb-24 flex justify-center" data-testid="blog-post-loading"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>;
  }

  return (
    <div className="pt-24 lg:pt-28 pb-20 bg-brand-canvas min-h-screen" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm font-bold text-brand-muted hover:text-brand-emerald transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <span className="inline-block bg-brand-emerald text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">{post.category}</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-emerald tracking-tight leading-tight">{post.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-brand-muted">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author || "Neat & Proper Team"}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.reading_time} min read</span>
          <span>{new Date(post.created_at).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full h-64 sm:h-96 object-cover rounded-3xl mt-8 shadow-lg" />
        )}
        <div className="rich-content mt-10" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-16" data-testid="comments-section">
        <h2 className="font-display text-2xl font-bold text-brand-emerald flex items-center gap-2 mb-8">
          <MessageSquare className="w-6 h-6 text-brand-lime" /> Comments ({comments.length})
        </h2>

        <div className="space-y-4 mb-10">
          {comments.length === 0 && <p className="text-brand-muted text-sm bg-white rounded-2xl border border-[#D6E4DC] p-6">Be the first to share your thoughts.</p>}
          {comments.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-[#D6E4DC] p-5" data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-brand-emerald text-brand-lime font-display font-bold flex items-center justify-center text-sm">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-sm text-brand-ink">{c.name}</p>
                  <p className="text-xs text-brand-muted">{new Date(c.created_at).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
              </div>
              <p className="text-sm text-brand-ink leading-relaxed">{c.content}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submitComment} className="bg-white rounded-3xl border border-[#D6E4DC] p-6 sm:p-8" data-testid="comment-form">
          <h3 className="font-display font-bold text-lg text-brand-emerald mb-5">Leave a comment</h3>
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <input data-testid="comment-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" className="px-4 py-3 rounded-xl border border-[#D6E4DC] text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50" />
            <input data-testid="comment-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional, never shown)" className="px-4 py-3 rounded-xl border border-[#D6E4DC] text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50" />
          </div>
          <textarea data-testid="comment-content-input" required minLength={2} rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Share your thoughts…" className="w-full px-4 py-3 rounded-xl border border-[#D6E4DC] text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50 mb-4" />
          <p className="text-xs text-brand-muted mb-4">Comments are moderated to keep the conversation clean — just like our spaces.</p>
          <button data-testid="comment-submit-btn" disabled={sending} className="px-6 py-3 rounded-full bg-brand-emerald text-white font-bold text-sm hover:bg-brand-lime disabled:opacity-50 transition-colors flex items-center gap-2">
            <Send className="w-4 h-4" /> {sending ? "Posting…" : "Post Comment"}
          </button>
        </form>
      </section>
    </div>
  );
}
